/**
 * Terminate Contract Dialog Component
 * US-CONT-006: Terminate Contract
 * Modal dialog for terminating contracts with confirmation
 */

import { useState, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ContractTerminationForm, useTerminationForm } from './ContractTerminationForm';
import type { ExpiringContract } from '@/features/contracts/expiring/types/expiringContracts.types';
import type { TerminationFormState } from '../types/contract-termination.types';
import { TERMINATION_REASON_LABELS } from '../types/contract-termination.types';

interface TerminateContractDialogProps {
  contract: ExpiringContract | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: TerminationFormState) => void | Promise<void>;
  isLoading?: boolean;
}

export function TerminateContractDialog({
  contract,
  open,
  onOpenChange,
  onConfirm,
  isLoading = false
}: TerminateContractDialogProps) {
  const { formData, isValid, handleFormChange, reset } = useTerminationForm();
  const [showConfirmation, setShowConfirmation] = useState(false);

  /**
   * Handle initial form submission
   * Shows confirmation dialog
   */
  function handleFormSubmit(data: TerminationFormState) {
    setShowConfirmation(true);
  }

  /**
   * Handle final confirmation
   * Executes termination
   */
  async function handleConfirm() {
    setShowConfirmation(false);
    await onConfirm(formData);
    reset();
  }

  /**
   * Handle dialog close
   */
  const handleClose = useCallback(() => {
    if (!isLoading) {
      reset();
      setShowConfirmation(false);
      onOpenChange(false);
    }
  }, [isLoading, onOpenChange, reset]);

  /**
   * Handle confirmation cancel
   */
  function handleConfirmationCancel() {
    setShowConfirmation(false);
  }

  if (!contract) return null;

  return (
    <>
      {/* Main Termination Form Dialog */}
      <Dialog open={open && !showConfirmation} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[600px]" aria-describedby="terminate-dialog-description">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                <XCircle className="h-6 w-6 text-destructive" aria-hidden="true" />
              </div>
              <div>
                <DialogTitle className="text-xl">Terminate Contract</DialogTitle>
                <DialogDescription id="terminate-dialog-description" className="text-sm">
                  Contract {contract.contractNumber}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Contract Details */}
          <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium">Customer</p>
                <p className="text-sm text-muted-foreground">{contract.customerName}</p>
                {contract.customerPhone && (
                  <p className="text-xs text-muted-foreground">{contract.customerPhone}</p>
                )}
              </div>
              <Badge variant="outline" className="text-xs">
                {contract.status}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2 border-t">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Start Date</p>
                <p className="text-sm">{new Date(contract.startDate).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">End Date</p>
                <p className="text-sm">{new Date(contract.endDate).toLocaleDateString()}</p>
              </div>
            </div>

            {contract.contractValue && (
              <div className="pt-2 border-t">
                <p className="text-xs font-medium text-muted-foreground">Contract Value</p>
                <p className="text-sm font-semibold">
                  ₹{contract.contractValue.toLocaleString('en-IN')}
                </p>
              </div>
            )}
          </div>

          {/* Warning Message */}
          <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-900 p-4">
            <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-orange-900 dark:text-orange-100">
                This action cannot be undone
              </p>
              <p className="text-sm text-orange-800 dark:text-orange-200">
                Terminating this contract will:
              </p>
              <ul className="text-sm text-orange-800 dark:text-orange-200 list-disc list-inside space-y-1 ml-2">
                <li>Change contract status to TERMINATED</li>
                <li>Block all new orders under this contract</li>
                <li>Send notification to the customer</li>
                <li>Create a permanent termination record</li>
              </ul>
            </div>
          </div>

          {/* Termination Form */}
          <ContractTerminationForm
            onSubmit={handleFormSubmit}
            onChange={handleFormChange}
            isLoading={isLoading}
          />

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => handleFormSubmit(formData)}
              disabled={!isValid || isLoading}
            >
              {isLoading ? 'Terminating...' : 'Terminate Contract'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden="true" />
              Confirm Contract Termination
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  You are about to terminate contract <strong>{contract.contractNumber}</strong> for{' '}
                  <strong>{contract.customerName}</strong>.
                </p>

                <div className="rounded-lg border bg-muted p-3 space-y-2 text-sm">
                  <div>
                    <span className="font-medium">Reason:</span>{' '}
                    <span className="text-muted-foreground">
                      {formData.reason ? TERMINATION_REASON_LABELS[formData.reason] : 'Not specified'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Details:</span>{' '}
                    <p className="text-muted-foreground mt-1 text-xs">
                      {formData.notes || 'No details provided'}
                    </p>
                  </div>
                </div>

                <p className="text-destructive font-medium">
                  This action is permanent and cannot be reversed.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleConfirmationCancel} disabled={isLoading}>
              Go Back
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={isLoading}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                isLoading && 'opacity-50 cursor-not-allowed'
              )}
            >
              {isLoading ? 'Terminating...' : 'Yes, Terminate Contract'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
